import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { Loading, Message, Page } from "../components/Page";
import { FreedomClarityBody, MeaningCard, ResultHero, ResultSection, formatDate } from "../components/ResultParts";
import { ResultBodyV4 } from "../components/ResultV4";
import { SaveResultsCard, SeeAllProfilesCard, useTakeAccount } from "./Account";
import { ThemeSwitch } from "../components/ThemeSwitch";
import { PersonalWriteup } from "../components/PersonalWriteup";
import { WhatOthersSee } from "../components/WhatOthersSee";
import { SendRatersNudge } from "../components/SendRatersNudge";
import { ApiError, api, getToken } from "../lib/api";
import { giftLine } from "../lib/copy";
import { keyFromHash, rememberResultKey, storedResultKey } from "../lib/resultKey";
import { HISTORY_MAX } from "../lib/map";
import { isV4Result, type DomainCode, type ResultView, type ResultViewV4 } from "../lib/types";
import { domainColor } from "../lib/domainColors";
import { useEmailEnabled } from "../lib/useEmailEnabled";
import { useScrollDepth } from "../lib/useScrollDepth";

// Still exported from here (it moved to ResultParts, which the v4 body shares).
export { formatDate };

export function Results() {
  const { takeId = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  // A key from the results email (#k=...) opens this take on any device.
  const [key] = useState(() => {
    const fromHash = keyFromHash(location.hash);
    if (fromHash) rememberResultKey(takeId, fromHash);
    return fromHash ?? storedResultKey(takeId);
  });
  const [result, setResult] = useState<ResultView | ResultViewV4 | null>(null);
  const [error, setError] = useState<"no_access" | "not_found" | "failed" | null>(null);
  const emailEnabled = useEmailEnabled() === true;

  // Strip the key from the address bar once it's kept.
  useEffect(() => {
    if (keyFromHash(location.hash)) navigate({ pathname: location.pathname, search: location.search }, { replace: true });
  }, [location, navigate]);

  useEffect(() => {
    let live = true;
    setResult(null);
    setError(null);
    api
      .getResult(takeId, key)
      .then((r) => live && setResult(r))
      .catch((e) => {
        if (!live) return;
        if (e instanceof ApiError && e.code === "not_submitted") navigate(`/take/${takeId}`, { replace: true });
        else if (e instanceof ApiError && (e.status === 404 || e.status === 422)) setError(key || getToken() ? "not_found" : "no_access");
        else setError("failed");
      });
    return () => {
      live = false;
    };
  }, [takeId, key, navigate]);

  if (error === "no_access") {
    return (
      <Message title="Your results">
        <p>
          {emailEnabled
            ? "Open the link from your results email to see this page."
            : "Your results open on the device you took the profile on. Open this page there."}
        </p>
        <p className="mt-6 flex flex-col gap-2 text-[15px]">
          <Link to="/sign-in">Sign in to see your profiles</Link>
          <Link to="/">Start a new profile</Link>
        </p>
      </Message>
    );
  }
  if (error === "not_found") {
    return (
      <Message title="We can't find these results">
        <p>
          {emailEnabled
            ? "The link may be incomplete. Open it again from your results email, or start a new profile."
            : "The link may be incomplete. Check it and try again, or start a new profile."}
        </p>
        <p className="mt-6 text-[15px]">
          <Link to="/">Start a new profile</Link>
        </p>
      </Message>
    );
  }
  if (error === "failed") {
    return (
      <Message title="Something went wrong">
        <p>We couldn't load your results. Check your connection and reload the page.</p>
      </Message>
    );
  }
  if (!result) return <Loading label="Loading your results" />;
  return <ResultPage result={result} resultKey={key} />;
}


export function ResultPage({ result, resultKey }: { result: ResultView | ResultViewV4; resultKey?: string | null }) {
  // Until the server says, assume not, so nothing promises an email.
  const emailEnabled = useEmailEnabled() === true;
  // How far down their results they read (once per quarter; never from the admin's copy).
  useScrollDepth(result.takeId, resultKey);
  // Opened from its results email (a key): that link shows only this profile, so "see all your
  // profiles" goes at the top (Edward, Nov 3); otherwise how to come back sits at the bottom.
  const account = useTakeAccount(result.takeId, resultKey);
  const fromEmail = !!resultKey;
  const top = fromEmail ? <SeeAllProfilesCard account={account} /> : null;
  // Scoring v4: its own body (with the v3.1 rater test's links and view). Its PDF is this page printed.
  if (isV4Result(result)) {
    return (
      <Page width="wide">
        <ResultBodyV4 result={result} resultKey={resultKey} emailEnabled={emailEnabled} top={top} />
        <SaveResultsCard account={account} fromEmail={fromEmail} />
        <footer className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <DownloadPdf result={result} resultKey={resultKey} />
          <ThemeSwitch />
        </footer>
      </Page>
    );
  }
  return (
    <Page width="wide">
      <ResultBody result={result} resultKey={resultKey} emailEnabled={emailEnabled} top={top} />
      <SaveResultsCard account={account} fromEmail={fromEmail} />
      <footer className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <DownloadPdf result={result} resultKey={resultKey} />
        <ThemeSwitch />
      </footer>
    </Page>
  );
}

/**
 * The results sections without the page shell. The admin reuses it read-only and passes
 * its own section 05 (`others`) so nothing here calls the taker-only rater endpoints.
 */
export function ResultBody({
  result,
  resultKey,
  others,
  emailEnabled = false,
  top,
}: {
  result: ResultView;
  resultKey?: string | null;
  others?: ReactNode;
  /** Under the hero, before anything else (the results page opened from its email: SeeAllProfilesCard). */
  top?: ReactNode;
  /** Results emails go out, so copy may point people to them. */
  emailEnabled?: boolean;
}) {
  const { record, display } = result;
  const domainNames = Object.fromEntries(display.domains.map((d) => [d.code, d.name])) as Record<DomainCode, string>;
  const primary = display.domains.find((d) => d.code === record.primary_domain);
  const domainTotal = display.domains.reduce((n, d) => n + d.count, 0) || 12;
  const domainsByCount = [...display.domains].sort((a, b) => b.count - a.count);
  const giftsByRank = [...record.gifts].sort((a, b) => a.rank - b.rank);
  const theme = display.themes.primary;
  const tiedTheme = display.themes.tied ? display.themes.secondary : null;
  const work = record.work_on.mode === "work";
  const topGift = record.top_gifts[0];
  const scoreOf = (gift: string): number | null => record.gifts.find((g) => g.gift === gift)?.score ?? null;

  return (
    <>
      {/* Hero */}
      <ResultHero
        name={display.profile.name}
        quadrant={record.quadrant}
        domainName={primary?.name ?? record.primary_domain}
        topGift={topGift}
      />

      {top}

      {/* The taker's own page (not the admin's copy): don't let them miss the rater links. */}
      {others ? null : <SendRatersNudge observer={result.observer} takeId={result.takeId} resultKey={resultKey} />}

      {/* 01 */}
      <ResultSection n={1} label="Where you are" title="Freedom × Clarity">
        <FreedomClarityBody
          record={record}
          domainNames={domainNames}
          series={result.series}
          history={result.history}
          emailEnabled={emailEnabled}
          takeId={result.takeId}
        />
      </ResultSection>

      {/* 02 */}
      <ResultSection
        n={2}
        label="How you're built"
        title={`${primary?.name ?? record.primary_domain}${primary?.line ? ` · ${primary.line}` : ""}`}
      >
        <p className="text-[17px] leading-snug">
          Your strongest domain is <strong>{primary?.name ?? record.primary_domain}</strong>
          {primary?.line ? `: ${primary.line}` : ""}.
        </p>
        <ul className="mt-5 flex flex-col gap-3" aria-label="Your domains">
          {domainsByCount.map((d) => {
            const isPrimary = d.code === record.primary_domain;
            return (
              <li key={d.code}>
                <div className="text-[15px]">
                  <span className={isPrimary ? "font-bold" : ""}>{d.name}</span>
                </div>
                <div className="mt-1.5 h-2.5 w-full rounded-full bg-soft" aria-hidden="true">
                  <div
                    className="h-full rounded-full"
                    data-domain={d.code}
                    style={{ width: `${(d.count / domainTotal) * 100}%`, backgroundColor: domainColor(d.code).fill }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      </ResultSection>

      {/* 03 */}
      <ResultSection n={3} label="Your top gifts" title={record.top_gifts.join(", ")}>
        <ol className="flex flex-col gap-3">
          {record.top_gifts.map((gift, i) => (
            <li key={gift} className="flex items-start gap-4 rounded-[16px] border border-line bg-panel p-4">
              <span className="numeral text-[34px] text-accent-text" aria-hidden="true">
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="heading text-[22px] leading-tight">
                  <span className="sr-only">{i + 1}. </span>
                  {gift}
                </h3>
                <p className="mt-1 text-[15px] leading-snug text-muted">{giftLine(display, gift)}</p>
              </div>
              {scoreOf(gift) !== null ? (
                <span className="numeral shrink-0 text-[24px]" aria-label={`Score ${scoreOf(gift)}`}>
                  {scoreOf(gift)}
                </span>
              ) : null}
            </li>
          ))}
        </ol>
        <details className="mt-4 rounded-[16px] border border-line bg-panel">
          <summary className="flex min-h-12 cursor-pointer items-center px-4 font-semibold">See all 10</summary>
          <ol className="border-t border-line px-4 py-2">
            {giftsByRank.map((g) => (
              <li key={g.gift} className="flex min-h-10 items-center justify-between gap-3 text-[15px]">
                <span>
                  <span className="inline-block w-7 text-muted">{g.rank}.</span>
                  {g.gift}
                </span>
                <span className="text-muted tabular-nums">{g.score}</span>
              </li>
            ))}
          </ol>
        </details>
      </ResultSection>

      {/* 04: the write-up, in an ink card */}
      <MeaningCard text={display.profile.youCore || display.profile.core} />

      {/* The personalized write-up (Addendum D). It builds on the profile above it. */}
      <PersonalWriteup takeId={result.takeId} writeup={result.writeup} resultKey={resultKey} readOnly={!!others} />

      {/* 05 */}
      <ResultSection n={5} label="What to work on" title={tiedTheme ? `${theme.name} and ${tiedTheme.name}` : theme.name}>
        {work && tiedTheme ? (
          <>
            {/* As good as tied: both themes, each with its meaning and first move. */}
            {[theme, tiedTheme].map((t) => (
              <div key={t.code} className="mt-4 first:mt-0" data-testid="work-theme">
                <h3 className="heading text-[19px]">{t.name}</h3>
                <p className="mt-1 text-[17px] leading-snug">{t.meaning}</p>
                <p className="mt-2 text-[17px] leading-snug">
                  <strong>First move:</strong> {t.firstMove}
                </p>
              </div>
            ))}
          </>
        ) : work ? (
          <>
            <p className="text-[17px] leading-snug">{theme.meaning}</p>
            <p className="mt-5 text-[17px] leading-snug">
              <strong>First move:</strong> {theme.firstMove}
            </p>
          </>
        ) : (
          <p className="text-[17px] leading-snug">{display.guardLine}</p>
        )}
        {display.clarityFocus ? (
          <p className="mt-4 text-[17px] leading-snug">
            <strong>Clarity focus:</strong> {display.clarityFocus}
          </p>
        ) : null}
        <p className="mt-4 text-[17px] leading-snug">
          <strong>Blind spot:</strong> {display.blindSpot}
        </p>
      </ResultSection>

      {/* 06 */}
      <ResultSection n={6} title="What others see">
        {others ?? <WhatOthersSee takeId={result.takeId} observer={result.observer} resultKey={resultKey} selfGifts={record.gifts} selfDomains={record.domain_counts} />}
      </ResultSection>

      {/* Codex */}
      {display.codex && display.codex.parts.length > 0 ? (
        <section className="mt-14 rounded-[16px] bg-soft p-5 sm:p-7" aria-labelledby="codex-title">
          <h2 id="codex-title" className="heading text-[26px]">
            {display.codex.title || "In one place"}
          </h2>
          <div className="mt-4 flex flex-col gap-3 text-[17px] leading-snug">
            {display.codex.parts.map((p) => (
              <p key={p.key}>
                <strong>{p.title}</strong> {p.text}
              </p>
            ))}
          </div>
          {display.codex.close ? <p className="heading mt-5 text-[20px] leading-snug">{display.codex.close}</p> : null}
        </section>
      ) : null}

    </>
  );
}

/** Saves the results PDF (the same one the results email carries). */
function DownloadPdf({ result, resultKey }: { result: Pick<ResultView, "takeId" | "firstName">; resultKey?: string | null }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const download = async () => {
    setBusy(true);
    setFailed(false);
    try {
      const { blob, fileName } = await api.resultPdf(result.takeId, resultKey);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // The server's name says which take it's from (event and date), as the emailed copy does.
      a.download = fileName ?? `Gifts Profile - ${result.firstName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-3">
      <button type="button" className="btn btn-secondary min-h-11 px-4 text-[14px]" onClick={() => void download()} disabled={busy}>
        {busy ? "Making your PDF…" : "Download PDF"}
      </button>
      {failed ? (
        <p role="status" className="text-[14px] text-muted">
          The PDF didn't download. Try again.
        </p>
      ) : null}
    </div>
  );
}
